import Lottie from 'lottie-react';
import { memo, useMemo } from 'react';
import { Focusable } from 'react-aria-components';

import dotsIcon from '@/assets/animated/dots-icon.json';
import { Flex, Heading, Text, Tooltip, TooltipTrigger } from '@/components/core';
import { STATUS_TO_NAME } from '@/constants';
import { useAgentRunTree } from '@/hooks';
import { toAgentRunId, useGetAgentRun } from '@/hooks/api';
import { capitalize, filterEventsFromLastRun } from '@/utils';

import { AlertFilledIcon, ArchiveIcon, CheckmarkFilledIcon, PendingReviewIcon, WaitingFilledIcon } from '../icons';

export type AgentRunStatusProps = {
  agentId?: string;
  runId?: string;
  compact?: boolean;
};

const IAgentRunStatus = ({ agentId, runId, compact }: AgentRunStatusProps) => {
  const { data: run } = useGetAgentRun(toAgentRunId(agentId, runId));
  const events = useAgentRunTree({ agentId, runId });

  const hasSkippedExports = useMemo(() => {
    const { lastExportEvents } = filterEventsFromLastRun(events);
    return lastExportEvents.some((event) => event.skipped);
  }, [events]);

  const { badge, tooltipText } = useMemo(() => {
    let badgeTextClassName;
    let badgeIcon;
    const badgeClassName = 'size-3';
    let tooltipText;

    switch (run?.status) {
      case 'archived':
        badgeIcon = <ArchiveIcon className={badgeClassName} />;
        tooltipText = 'This run has been archived.';
        break;
      case 'completed':
      case 'exported':
        badgeTextClassName = 'text-success-900';
        badgeIcon = <CheckmarkFilledIcon variant="success" className={badgeClassName} />;
        tooltipText = 'This run has been completed successfully. See events for details.';
        break;
      case 'error':
        badgeTextClassName = 'text-error-900';
        badgeIcon = <AlertFilledIcon variant="error" className={badgeClassName} />;
        tooltipText = 'An error occurred during this run. See events for details.';
        break;
      case 'ready-for-review':
        badgeTextClassName = 'text-warning-900';
        badgeIcon = <PendingReviewIcon variant="warning" className={badgeClassName} />;
        tooltipText =
          'The AI response has has been flagged for review. A human review is needed before it can be exported.';
        break;
      case 'review-in-progress':
        badgeTextClassName = 'text-primary-900';
        badgeIcon = <WaitingFilledIcon variant="primary" className={badgeClassName} />;
        tooltipText = 'This run is currently being reviewed.';
        break;
      case 'review-completed':
      case 'succeeded-predictions':
        badgeTextClassName = 'text-success-900';
        badgeIcon = <CheckmarkFilledIcon variant="success" className={badgeClassName} />;
        tooltipText = 'This run has a legacy status. See events for details.';
        break;
      case 'pending-export':
      case 'pending-predictions':
        badgeTextClassName = 'text-warning-900';
        badgeIcon = <AlertFilledIcon variant="warning" className={badgeClassName} />;
        tooltipText = 'This run is stuck, rerun from start to unstuck.';
        break;
      default:
        badgeTextClassName = 'text-primary-900';
        badgeIcon = <Lottie animationData={dotsIcon} className={badgeClassName} />;
        tooltipText = 'The agent is processing the document.';
    }

    const badge = compact ? (
      badgeIcon
    ) : (
      <Flex
        role="button"
        tabIndex={-1}
        variant="secondary"
        className="flex items-center gap-1.5 rounded-sm px-1.5 py-0.5"
      >
        {badgeIcon}
        <Text className={badgeTextClassName} size="xs">
          {STATUS_TO_NAME[run?.status ?? ''] ?? capitalize(run?.status)}
        </Text>
      </Flex>
    );

    return { badge, tooltipText };
  }, [compact, run]);

  return (
    <TooltipTrigger>
      <Tooltip variant="primary" className="gap-xs flex w-48 flex-col">
        <Heading className="border-gray-700" size="xs">
          {STATUS_TO_NAME[run?.status ?? ''] ?? run?.status}
        </Heading>
        <Text>{tooltipText}</Text>
      </Tooltip>
      <Focusable>{badge}</Focusable>
    </TooltipTrigger>
  );
};

export const AgentRunStatus = memo(IAgentRunStatus) as typeof IAgentRunStatus;
